import {QueryClient} from '@tanstack/react-query';
import {z as r} from 'zod';

import {ReviewDataType} from './reviews-type';

const ratingSortPaginationCacheDurationMs = 15 * 60 * 1000;
const ratingSortPaginationStoragePrefix = 'reviews_rating_sort_';

const ratingSortBucketCursorsSchema = r.record(r.string(), r.string());

const ratingSortPageSnapshotStorageSchema = r.object({
    buffer: r.array(r.unknown()),
    bucketIndex: r.number(),
    bucketCursors: ratingSortBucketCursorsSchema,
});

const ratingSortPaginationStoreStorageSchema = r.object({
    pageStartSnapshots: r.array(ratingSortPageSnapshotStorageSchema),
    hasNextPage: r.boolean(),
    timestamp: r.number(),
});

export type RatingSortPageSnapshotType = {
    buffer: Array<ReviewDataType>;
    bucketIndex: number;
    bucketCursors: Record<string, string>;
};

type RatingSortPaginationStoreType = {
    pageStartSnapshots: Array<RatingSortPageSnapshotType>;
    hasNextPage: boolean;
    timestamp: number;
};

function getRatingSortPaginationStorageKey(storageKey: string): string {
    return `${ratingSortPaginationStoragePrefix}${storageKey}`;
}

function parseSnapshotFromStorage(
    raw: r.infer<typeof ratingSortPageSnapshotStorageSchema>
): RatingSortPageSnapshotType {
    return {
        buffer: raw.buffer as Array<ReviewDataType>,
        bucketIndex: raw.bucketIndex,
        bucketCursors: raw.bucketCursors,
    };
}

function getRatingSortQueryKeyPrefix(storageKey: string): string {
    return `rating-sort:${storageKey}:`;
}

function invalidateRatingSortQueryCache(queryClient: QueryClient, storageKey: string): void {
    const prefix = getRatingSortQueryKeyPrefix(storageKey);

    queryClient.removeQueries({
        predicate: (query) => {
            const key = query.queryKey[0];

            return typeof key === 'string' && key.startsWith(prefix);
        },
    });
}

export function getEmptyRatingSortPageSnapshot(): RatingSortPageSnapshotType {
    return {
        buffer: [],
        bucketIndex: 0,
        bucketCursors: {},
    };
}

function isEmptyRatingSortPageSnapshot(snapshot: RatingSortPageSnapshotType): boolean {
    return (
        snapshot.bucketIndex === 0 && snapshot.buffer.length === 0 && Object.keys(snapshot.bucketCursors).length === 0
    );
}

export function isRatingSortPageStartSnapshotValid(store: RatingSortPaginationStoreType | null, page: number): boolean {
    return (
        page <= 1 ||
        (store !== null &&
            Boolean(store.pageStartSnapshots[page - 1]) &&
            !isEmptyRatingSortPageSnapshot(store.pageStartSnapshots[page - 1] as RatingSortPageSnapshotType))
    );
}

export function resetRatingSortPagination(storageKey: string, queryClient?: QueryClient): void {
    sessionStorage.removeItem(getRatingSortPaginationStorageKey(storageKey));

    if (queryClient) {
        invalidateRatingSortQueryCache(queryClient, storageKey);
    }
}

export function loadRatingSortPagination(storageKey: string): RatingSortPaginationStoreType | null {
    const storedValue = sessionStorage.getItem(getRatingSortPaginationStorageKey(storageKey));
    const parseResult = ratingSortPaginationStoreStorageSchema.safeParse(JSON.parse(storedValue || '{}'));

    if (!parseResult.success || Date.now() - parseResult.data.timestamp >= ratingSortPaginationCacheDurationMs) {
        return null;
    }

    return {
        pageStartSnapshots: parseResult.data.pageStartSnapshots.map(parseSnapshotFromStorage),
        hasNextPage: parseResult.data.hasNextPage,
        timestamp: parseResult.data.timestamp,
    };
}

export function saveRatingSortPagination(
    storageKey: string,
    data: Omit<RatingSortPaginationStoreType, 'timestamp'>
): void {
    sessionStorage.setItem(
        getRatingSortPaginationStorageKey(storageKey),
        JSON.stringify({
            ...data,
            timestamp: Date.now(),
        })
    );
}

export function getRatingSortPageStartSnapshot(
    store: RatingSortPaginationStoreType | null,
    page: number
): RatingSortPageSnapshotType {
    if (!store || page <= 1) {
        return getEmptyRatingSortPageSnapshot();
    }

    return store.pageStartSnapshots[page - 1] || getEmptyRatingSortPageSnapshot();
}

export function buildRatingSortPageStartSnapshots(
    previousSnapshots: Array<RatingSortPageSnapshotType>,
    page: number,
    endSnapshot: RatingSortPageSnapshotType
): Array<RatingSortPageSnapshotType> {
    return [...previousSnapshots.slice(0, page), endSnapshot];
}
